
export interface User {
    id: number;                // 用户ID
    username: string;          // 用户名,账户
    nickname?: string;         // 昵称
    avatar_url?: string;       // 头像
    provider_source?: string;  // 信息来源 （wechat）
    free_count: number;        // 免费次数
    email?: string;             // 会员邮箱（唯一）
    phone?: string;            // 会员电话
    sex?: 0 | 1 | 2;           // 性别: 0 未知, 1 男, 2 女
    lang?: string;
    city?: string;
    province?: string;
    created_at?: string;       // 注册时间
}


export enum ProductType {
    MONTH = 'MONTH',
    QUARTER = 'QUARTER',
    YEAR = 'YEAR',
    THREE_YEAR = '3YEAR',
}
// 产品信息接口
export interface Product {
    id: number; // 产品ID
    name: string; // 产品名称
    description: string; // 产品描述
    price: number; // 产品价格
    subscription_duration: number; // 订阅时长 (单位：天)
    product_type: ProductType; // 产品类型
    created_at: string; // 产品创建时间 (ISO 8601 格式的时间戳)
};

export enum PaymentStatus {
    // 支付状态
    // WAIT_BUYER_PAY: 交易创建，等待买家付款
    // TRADE_CLOSED: 未付款交易超时关闭，或支付完成后全额退款
    // TRADE_SUCCESS: 交易支付成功
    // TRADE_FINISHED: 交易结束，不可退款
    WAIT_BUYER_PAY = 'WAIT_BUYER_PAY',
    TRADE_CLOSED = 'TRADE_CLOSED',
    TRADE_SUCCESS = 'TRADE_SUCCESS',
    TRADE_FINISHED = 'TRADE_FINISHED',
}

export enum PaymentMethod {
    // 支付方式
    // WECHAT: 微信支付
    // ALIPAY: 支付宝支付
    // UNIONPAY: 银联支付
    // ...
    WECHAT = 'WECHAT',
    ALIPAY = 'ALIPAY',
    UNIONPAY = 'UNIONPAY',

}
export interface Order {
    id: number;                     // 订单ID
    user_id: number | null;         // 用户ID，关联到 `users` 表
    product_id: number | null;      // 产品ID，关联到 `products` 表
    product_type: ProductType;      // 产品类型
    order_number: string;           // 订单号（唯一）
    amount: number;                 // 订单金额，精确到两位小数
    payment_method?: PaymentMethod | null; // 支付方式（如：微信支付，支付宝等）
    payment_status: PaymentStatus;  // 支付状态
    payment_time?: string | null;   // 支付时间，ISO格式字符串
    created_at: string;             // 订单创建时间，ISO格式字符串
}

export interface Subscription {
    id: number; // 订阅ID
    user_id: number; // 用户ID，关联到 `users` 表
    order_id: number; // 订单ID，关联到 `orders` 表
    subscription_start: string; // 订阅开始时间 (ISO 8601 格式的日期字符串)
    subscription_end: string; // 订阅结束时间 (ISO 8601 格式的日期字符串)
    subscription_type: ProductType; // 订阅类型
}

/**
 * 批量照片记录接口
 * 对应数据库表 batch_photo_records
 */
export interface BatchPhotoRecord {
    id: number;                 // 记录ID
    user_id: number;            // 关联的用户ID
    total_count: number;        // 总个数（成功、失败、未处理）
    success_count: number;      // 处理成功个数
    fail_count: number;         // 处理失败个数
    use_time: string | Date;    // 使用时间，可根据实际情况选择string或Date
    batch_type: string | null;  // 批量类型（如：export replace）
}

const productTypeTextMap = {
    [ProductType.MONTH]: '一个月',
    [ProductType.QUARTER]: '三个月',
    [ProductType.YEAR]: '一年',
    [ProductType.THREE_YEAR]: '三年',
}

const paymentStatusTextMap = {
    [PaymentStatus.WAIT_BUYER_PAY]: '待支付',
    [PaymentStatus.TRADE_CLOSED]: '已关闭',
    [PaymentStatus.TRADE_SUCCESS]: '已完成',
    [PaymentStatus.TRADE_FINISHED]: '已完成',
}

export function getProductTypeText(productType: ProductType) {
    return productTypeTextMap[productType]
}

export function getPaymentStatusText(paymentStatus: PaymentStatus) {
    return paymentStatusTextMap[paymentStatus]
}

const threshold = 240; // 颜色接近白色的阈值
export class RGBA {
    r: number;
    g: number;
    b: number;
    a: number;

    constructor(r: number, g: number, b: number, a: number) {
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;
    }

    /**
     * 
     * @param hex 为十六进制颜色值，如：#ff0000
     * @returns RGBA对象
     */
    static fromHex(hex: string): RGBA {
        // 去掉前导的 '#' 符号
        hex = hex.replace(/^#/, '');

        // 处理简写形式的颜色值（如：#f00）
        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('');
        }

        // 解析颜色值
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return new RGBA(r, g, b, 1);
    }

    toRgbaString(): string {
        return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`
    }

    toHexString(): string {
        return `#${this.r.toString(16)}${this.g.toString(16)}${this.b.toString(16)}`
    }

    isLight(): boolean {
        return this.r > threshold && this.g > threshold && this.b > threshold;
    }

}

export enum PositionType {
    LeftTop, // 水印左上角相对于图片左上角
    LeftBottom, // 水印左下角相对于图片左下角
    RightTop, // 水印右上角相对于图片右上角
    RightBottom, // 水印右下角相对于图片右下角
    Center,    // 水印中心相对于图片中心
    Bottom,    // 水印中心水平居中，水印底部相对于图片底部
    Top,       // 水印中心水平居中，水印顶部相对于图片顶部
    Flat,     // 平铺
}


export const postionOption = [
    { label: '左上角', value: PositionType.LeftTop },
    { label: '左下角', value: PositionType.LeftBottom },
    { label: '右上角', value: PositionType.RightTop },
    { label: '右下角', value: PositionType.RightBottom },
    { label: '底部', value: PositionType.Bottom },
    { label: '顶部', value: PositionType.Top },
    { label: '中心', value: PositionType.Center },
    { label: '平铺', value: PositionType.Flat },
]

export function getPositionTypeText(positionType: PositionType) {
    switch (positionType) {
        case PositionType.LeftTop:
            return '左上角'
        case PositionType.LeftBottom:
            return '左下角'
        case PositionType.RightTop:
            return '右上角'
        case PositionType.RightBottom:
            return '右下角'
        case PositionType.Center:
            return '中心'
        case PositionType.Bottom:
            return '底部'
        case PositionType.Top:
            return '顶部'
        case PositionType.Flat:
            return '平铺'
        default:
            return ''
    }
}